/**
 * OAuth `state` CSRF protection (RFC 6749 §10.12).
 *
 * The login redirect carries a random, per-login nonce inside the OAuth `state` param and stores
 * the same value for this browser tab. The callback must confirm the returned nonce matches the
 * stored one BEFORE exchanging the authorization code — otherwise a crafted callback URL carrying
 * an attacker-generated code would let this session store the attacker's tokens, and the buyer's
 * purchases / saved cards would be applied to the wrong account.
 *
 * sessionStorage is tab-scoped, which is exactly the right scope: the nonce belongs to the login
 * attempt started in this tab, and the OAuth redirect returns to the same tab.
 */
const STORAGE_KEY = 'pb_oauth_state_nonce';

function randomNonce(): string {
    const c = (globalThis as any).crypto;
    if (c?.randomUUID) return c.randomUUID();
    // Non-crypto fallback (very old/edge runtimes) — still unguessable enough for CSRF state.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/** Generate a per-login nonce and persist it for this tab until the callback consumes it. */
export function createOAuthStateNonce(): string {
    const nonce = randomNonce();
    try {
        sessionStorage.setItem(STORAGE_KEY, nonce);
    } catch {
        // sessionStorage unavailable — the callback's consume will then fail closed (reject).
    }
    return nonce;
}

/**
 * Read and clear (one-time) the stored nonce, then report whether it matches the nonce the callback
 * received in `state`. Fails closed: a missing/empty/mismatched nonce returns false so the caller
 * rejects the callback before any token exchange.
 */
export function consumeOAuthStateNonce(returnedNonce: unknown): boolean {
    let stored: string | null = null;
    try {
        stored = sessionStorage.getItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        stored = null;
    }
    return typeof returnedNonce === 'string' && returnedNonce.length > 0 && returnedNonce === stored;
}
